/**
 * "Prefill from a template variable" for a form-template field.
 *
 * Sets the field's `data_source` to `variable.<key>` (BE #1220): when a form
 * instance is started, the field is prefilled from the org's template-variable
 * registry (variables that apply to forms). An `input` variable is asked of
 * staff when they start the form; one that cannot be filled is left empty.
 */

import React, { useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useVariableRegistry } from '@/components/messaging/templateVariables/useVariableRegistry';
import { categoryLabel, sortCategories } from '@/components/messaging/templateVariables/labels';
import type { TemplateVariable } from '@/components/messaging/templateVariables/api';

export const VARIABLE_SOURCE_PREFIX = 'variable.';

export const isVariableSource = (ds: string | null | undefined): boolean =>
  typeof ds === 'string' && ds.startsWith(VARIABLE_SOURCE_PREFIX);

interface FormFieldVariableSourceProps {
  value: string | null | undefined;
  onChange: (dataSource: string | undefined) => void;
  id?: string;
}

export function FormFieldVariableSource({ value, onChange, id = 'field-variable-source' }: FormFieldVariableSourceProps) {
  const { t } = useAppTranslation();
  const { variables, loading } = useVariableRegistry('forms');
  const current = isVariableSource(value) ? (value as string).slice(VARIABLE_SOURCE_PREFIX.length) : '';

  const groups = useMemo(() => {
    const byCat = new Map<string, TemplateVariable[]>();
    for (const v of variables) {
      const cat = v.category || 'general';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(v);
    }
    return sortCategories([...byCat.keys()]).map((cat) => ({ cat, items: byCat.get(cat)! }));
  }, [variables]);

  const known = loading || !current || variables.some((v) => v.key === current);

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {t('templateVariables.forms.prefillLabel', 'Prefill from a template variable')}
      </label>
      <select
        id={id}
        value={current}
        disabled={loading}
        onChange={(e) => onChange(e.target.value ? `${VARIABLE_SOURCE_PREFIX}${e.target.value}` : undefined)}
        className="w-full rounded-md border border-v2border bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-v2accent"
      >
        <option value="">{t('templateVariables.forms.none', 'None — staff fill it in')}</option>
        {!known && <option value={current}>{`{{${current}}}`}</option>}
        {groups.map(({ cat, items }) => (
          <optgroup key={cat} label={categoryLabel(t, cat)}>
            {items.map((v) => (
              <option key={`${v.scope}:${v.key}`} value={v.key}>
                {`${v.label || v.key} — {{${v.key}}}`}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="mt-1 text-xs text-ink-500">
        {t(
          'templateVariables.forms.prefillHelp',
          'When a form is started, this field is filled from the variable. A variable entered by staff is asked for when the form starts; one with no value is left empty.',
        )}
      </p>
      {!known && (
        <p role="alert" className="mt-1 text-xs text-danger">
          {t('templateVariables.forms.unknown', 'This variable is not available for forms in your organization. Choose another.')}
        </p>
      )}
    </div>
  );
}
